import { HMSActions } from "@100mslive/react-sdk";
import { makeAutoObservable } from "mobx";
import { wrap } from "../util/supabase-wrapper";
import backendFetch from "./customFetch/customFetch";
import { globalStores } from "./globalStores";
import { definitions, supabase } from "./supabase/client";
import teacherStore from "./teacher";
import { TeacherFeedback } from "./teacherFeedback";
import { observeUrlChange } from "../Common/Utility/onUrlChange";

const logger = console;

const isMobileOrTablet = () => {
  return /iPad|iPhone|iPod|Android/i.test(navigator.userAgent) ||
    (navigator.userAgent.includes("Mac") && "ontouchend" in document);
};

/** How long (ms) to suppress realtime overwriting of currentEngagementPanel after a local setEPanel call. */
const PANEL_SWITCH_GUARD_MS = 3000;

export class TalkingParty {
  screenShared: boolean = false;
  microphoneOn: boolean = false;
  talkingParty: Partial<
    Omit<definitions["TalkingPartyRoom"], "startTime" | "endTime"> & {
      startTime: Date;
      endTime: Date;
    }
  > = {};
  twilioToken: string | undefined;
  timeSlot: Partial<definitions["TimeSlot"]> = {};
  room: undefined;
  module: Partial<definitions["Module"]> | null = {};
  engagementPanelResults: Partial<definitions["EngagementPanelResult"]>[] = [];
  talkingPartyStatuses: Record<
    string,
    Partial<definitions["TalkingPartyStatus"]>
  > = {};
  hmsActions: HMSActions | undefined;
  feeling: string | undefined;
  degradedTracks: Set<string> = new Set();

  // Timestamp of the last local setEPanel call; used to prevent realtime
  // pullDetails from overwriting the panel ID with a stale DB value.
  private _lastPanelSetAt: number = 0;

  constructor() {
    makeAutoObservable(this, {
      _lastPanelSetAt: false,
    } as any);
  }

  setHMSActions(newHMSActions: HMSActions) {
    this.hmsActions = newHMSActions;
  }

  setTrackDegraded(trackId: string, isDegraded: boolean) {
    if (isDegraded) {
      if (this.degradedTracks.has(trackId)) return;
      this.degradedTracks.add(trackId);
    } else {
      if (!this.degradedTracks.has(trackId)) return;
      this.degradedTracks.delete(trackId);
    }
  }

  isTrackDegraded(trackId?: string) {
    if (!trackId) return false;
    return this.degradedTracks.has(trackId);
  }

  async joinActiveLessonForClass(classId: number) {
    // Tell the server which of the parent's children is joining — without
    // this, a parent with siblings in the same class would have both browsers
    // receive the same studentId in the JWT (the server picks studentRows[0])
    // and both 100ms peers would render with the same name. Mirrors the
    // legacy /generate-100ms-token identity payload at getAccessToken().
    const childId = globalStores?.childStore?.childAccount?.id ?? null;
    const result = await backendFetch("100ms/get-active-room", "POST", {
      classId,
      ...(childId != null && { childId }),
    });

    if (!result.ok) {
      const errorText = await result.text();
      logger.error("joinActiveLessonForClass: backend rejected", errorText);
      throw new Error("Couldn't load your lesson room. Please try again.");
    }

    const payload = await result.json();
    const data = payload?.data;

    if (!data) {
      throw new Error(payload?.err ?? "Unexpected response from server");
    }

    if (data.status === "no_active_lesson") {
      throw new Error(
        "There's no class to join right now. Try again closer to your class time."
      );
    }

    if (!data.talkingPartyId || !data.hmsToken) {
      throw new Error("Server returned an incomplete lesson room");
    }

    this.talkingParty.id = data.talkingPartyId;
    this.twilioToken = data.hmsToken;

    await this.pullDetails();

    logger.log("Joined talking party", data.talkingPartyId, data.status);
    return this;
  }

  async submitChildFeedback(rating: number) {
    if (!globalStores.childStore.childAccount.id) {
      throw new Error(
        "Child Store must be initialized to submit child feedback"
      );
    }
    if (!this.talkingParty.id) {
      throw new Error(
        "Talking Party must be initialized to submit child feedback"
      );
    }
    await supabase
      .from("ClassFeedback")
      .insert({
        child: globalStores.childStore.childAccount.id,
        talkingParty: this.talkingParty.id,
        rating: rating,
      })
      .throwOnError();
  }

  async init(id: number) {
    this.talkingParty.id = id;
    await this.pullDetails();
    await this.getAccessToken();

    return this;
  }

  refresh() {
    this.pullDetails();
  }

  /** Update local panel state immediately and arm the realtime guard. No DB/HMS calls. */
  setEPanelLocal(id: number) {
    this._lastPanelSetAt = Date.now();
    if (this.talkingParty.currentEngagementPanel !== id) {
      this.talkingParty.currentEngagementPanel = id;
    }
  }

  async setEPanel(id: number) {
    this._lastPanelSetAt = Date.now();

    if (this.talkingParty.currentEngagementPanel !== id) {
      this.talkingParty.currentEngagementPanel = id;
    }

    try {
      if (this.hmsActions?.sessionStore) {
        logger.log("HMS Session Store: Setting currentEngagementPanelId to", id);
        await this.hmsActions.sessionStore.set("currentEngagementPanelId", id);
      } else {
        logger.warn("HMS Session Store: Actions or store not available for setting ID");
      }
    } catch (error) {
      logger.error("Failed to update HMS Session Store:", error);
    }

    await supabase
      .from("TalkingPartyRoom")
      .update({ currentEngagementPanel: id })
      .match({ id: this.talkingParty.id })
      .throwOnError();
  }

  async setModule(id: number) {
    if (this.talkingParty.module !== id) {
      this.talkingParty.module = id;
    }

    try {
      if (this.hmsActions?.sessionStore) {
        logger.log("HMS Session Store: Setting currentModuleId to", id);
        await this.hmsActions.sessionStore.set("currentModuleId", id);
      }
    } catch (error) {
      logger.error("Failed to update HMS Session Store (Module):", error);
    }

    //TODO: Only allow teacher to update Module
    console.warn("Set Module has ran!");
    await supabase
      .from("TalkingPartyRoom")
      .update({ module: id })
      .match({ id: this.talkingParty.id })
      .throwOnError();
    await globalStores.curModule.init(id);
  }

  async showAnswer(show: boolean) {
    if (this.talkingParty.showAnswerForEngagementPanel !== show) {
      this.talkingParty.showAnswerForEngagementPanel = show;
    }

    try {
      if (this.hmsActions?.sessionStore) {
        logger.log("HMS Session Store: Setting showAnswer to", show);
        await this.hmsActions.sessionStore.set("showAnswer", show);
      }
    } catch (error) {
      logger.error("Failed to update HMS Session Store (ShowAnswer):", error);
    }

    await supabase
      .from("TalkingPartyRoom")
      .update({ showAnswerForEngagementPanel: show })
      .match({ id: this.talkingParty.id })
      .throwOnError();
  }

  async getAccessToken() {

    if (!this.talkingParty.roomID) {
      throw new Error(
        "Can't start talking party without first setting twilio room id!"
      );
    }

    let studentId = globalStores?.childStore?.childAccount?.id ?? null;
    let teacherId = teacherStore?.teacherAccount?.id ?? null;

    if (teacherId && studentId) {
      throw new Error(
        "Logged in as both a student and teacher! Please logout and log back in"
      );
    }

    const result = await backendFetch("100ms/generate-100ms-token", "POST", {
      room: this.talkingParty.roomID,
      identity: JSON.stringify({
        teacherId,
        studentId,
      }),
    });

    if (!result.ok) {
      const errorText = await result.text();
      logger.error('Failed to fetch 100ms token:', errorText);
      throw new Error(`Failed to fetch 100ms token: ${result.status} ${result.statusText}`);
    }

    const data = await result.json();

    if (data?.data?.token) {
      this.twilioToken = data.data.token;
    } else {
      logger.error('No token in response data:', data);
      throw new Error("Received empty token from backend");
    }
  }

  async joinRoom() {
    if (!this.twilioToken)
      throw new Error("Need to have Twilio Token before joining a room!");

    logger.log('🔊 TALKING PARTY DEBUG: Starting HMS room join process...');
    logger.log('🔊 TALKING PARTY DEBUG: Token available:', !!this.twilioToken);
    logger.log('🔊 TALKING PARTY DEBUG: HMS Actions available:', !!this.hmsActions);

    if (!this.hmsActions) {
      throw new Error("HMS Actions not initialized. Cannot join room.");
    }

    const isTablet = isMobileOrTablet();

    await this.hmsActions.join({
      userName: "",
      authToken: this.twilioToken,
      settings: {
        isAudioMuted: false,
        isVideoMuted: false,
        ...(isTablet && {
          videoDeviceId: undefined,
          audioDeviceId: undefined,
        }),
      },
      initEndpoint: "https://prod-init.100ms.live/init",
      rememberDeviceSelection: !isTablet,
    });

    // Constrain outgoing video for tablets to reduce CPU/memory/bandwidth.
    // Dimensions are landscape (480x360, 4:3) because students hold iPads in
    // landscape during class — portrait dims here lock the encoded track to
    // portrait for every viewer regardless of device rotation.
    if (isTablet && this.hmsActions) {
      try {
        await this.hmsActions.setLocalVideoEnabled(true);
        await this.hmsActions.setVideoSettings({
          width: 480,
          height: 360,
          maxFramerate: 15,
          maxBitrate: 200,
        });
        logger.log("Tablet detected – video constrained to 360p/15fps/200kbps");
      } catch (err) {
        logger.warn("Failed to apply tablet video constraints:", err);
      }
    }

    logger.log('🔊 TALKING PARTY DEBUG: HMS join completed');

    if (globalStores?.childStore?.childAccount.talkingPartyStatus) {
      logger.log('🔊 TALKING PARTY DEBUG: Updating talking party status...');
      await supabase
        .from("TalkingPartyStatus")
        .update({ room: this.talkingParty.id })
        .match({
          id: globalStores.childStore.childAccount.talkingPartyStatus,
        })
        .throwOnError();
      logger.log('🔊 TALKING PARTY DEBUG: Talking party status updated');
    }

    logger.log('🔊 TALKING PARTY DEBUG: Room join process completed successfully');
    return this;
  }

  async leaveRoom() {
    this.hmsActions?.leave();
    this.cleanupSessionMemory();
  }

  /**
   * Release all in-memory state accumulated during a lesson.
   * Called automatically on leaveRoom so that File objects, panel data,
   * and other heap-heavy structures become eligible for GC.
   */
  cleanupSessionMemory() {
    logger.log("[Cleanup] Releasing lesson session memory");

    this.twilioToken = undefined;
    this.screenShared = false;
    this.microphoneOn = false;
    this.feeling = undefined;
    this.degradedTracks.clear();
    this.engagementPanelResults = [];
    this.talkingPartyStatuses = {};
    this.module = {};
    this.talkingParty = {};

    // Clear the current module and its preloaded panels (releases File objects)
    globalStores.curModule.clear();

    // Clear the current engagement panel (releases its File objects)
    globalStores.curEPanel.clear();

    logger.log("[Cleanup] Session memory released");
  }

  async toggleVideo(state: boolean) {
    if (!this.hmsActions) throw new Error("HMS ACTIONS NOT DEFINED");

    this.hmsActions?.setLocalVideoEnabled(state);
  }

  async toggleMicrophone(state: boolean) {
    if (!this.hmsActions) throw new Error("HMS ACTIONS NOT DEFINED");

    this.microphoneOn = state;

    this.hmsActions?.setLocalAudioEnabled(state);
  }

  _setScreenShared(state: boolean) {
    this.screenShared = state;
  }

  async toggleShareScreen(state: boolean) {
    this._setScreenShared(state);
    await this.hmsActions?.setScreenShareEnabled(state);
  }
  async muteAll() {
    await supabase
      .from("TalkingPartyRoom")
      .update({ muteAll: false })
      .match({ id: this.talkingParty.id });
    await supabase
      .from("TalkingPartyRoom")
      .update({ muteAll: true })
      .match({ id: this.talkingParty.id });
    await supabase
      .from("TalkingPartyRoom")
      .update({ muteAll: false })
      .match({ id: this.talkingParty.id });
  }
  async clearHands() {
    await supabase
      .from("TalkingPartyStatus")
      .update({ handRaised: false })
      .match({ room: this.talkingParty.id });

    return this;
  }

  async createTeacherFeedback() {
    let teacherFeedbackObj: definitions["TeacherFeedback"][];

    teacherFeedbackObj = wrap(
      await supabase
        .from("TeacherFeedback")
        .select("*")
        .match({ talkingPartyRoom: this.talkingParty.id })
    );

    if (teacherFeedbackObj.length === 0) {
      teacherFeedbackObj = wrap(
        await supabase
          .from("TeacherFeedback")
          .insert({
            talkingPartyRoom: this.talkingParty.id,
          })
          .select()
      );
    }

    const teacherFeedback = new TeacherFeedback();

    await teacherFeedback.init(teacherFeedbackObj[0].id);

    return teacherFeedback;
  }

  async setFeeling(feeling: string) {

    this.feeling = feeling;
  }

  async setHandRaised(raised: boolean) {
    if (!globalStores.childStore.childAccount.talkingPartyStatus) {
      throw new Error("Child must be logged in before setting hand raised!");
    }

    await supabase
      .from("TalkingPartyStatus")
      .update({ handRaised: raised })
      .match({ id: globalStores.childStore.childAccount.talkingPartyStatus })
      .throwOnError();

    await this.pullDetails();
  }

  private setAllDetails(allDetails: any[]) {
    // Preserve the locally-set panel ID if we recently switched panels,
    // so that a stale realtime/polling response doesn't overwrite it.
    const preservePanel =
      Date.now() - this._lastPanelSetAt < PANEL_SWITCH_GUARD_MS &&
      this.talkingParty.currentEngagementPanel != null;
    const localPanelId = this.talkingParty.currentEngagementPanel;

    this.talkingParty = allDetails[0];
    this.talkingParty.startTime = new Date(allDetails[0].startTime);
    this.talkingParty.endTime = new Date(allDetails[0].endTime);
    this.talkingParty.timeSlot = allDetails[0].TimeSlot;

    if (preservePanel) {
      this.talkingParty.currentEngagementPanel = localPanelId;
    }

    if (allDetails[0].EngagementPanelResult) {
      this.engagementPanelResults = allDetails[0].EngagementPanelResult;
    }
    this.module = allDetails[0].Module;
    if (allDetails[0].TalkingPartyStatus) {
      this.talkingPartyStatuses = allDetails[0].TalkingPartyStatus.reduce(
        (
          acc: Record<string, Partial<definitions["TalkingPartyStatus"]>>,
          newVal: Partial<definitions["TalkingPartyStatus"]>
        ) => {
          if (!newVal.id) return acc;
          acc[newVal.id] = newVal;
          return acc;
        },
        {}
      );
    }
  }

  async pullDetails() {
    const allDetails = wrap(
      await supabase
        .from("TalkingPartyRoom")
        .select(
          "*,TimeSlot(*),Module(*,topic:Topic(*,level:Level(*)))"
        )
        .match({ id: this.talkingParty.id })
    );

    this.setAllDetails(allDetails);

    return this;
  }
}

const curTalkingParty = new TalkingParty();

export default curTalkingParty;
